import { useEffect, useState } from 'preact/hooks';
import { useI18n, LOCALES, type Locale } from '../i18n';
import { RequirePartner } from '../components/RequirePartner';
import { PartnerHoursEditor, validateHours } from '../components/PartnerHoursEditor';
import { useAuth } from '../lib/auth';
import {
  getMyPartner,
  updateMyPartner,
  uploadFile,
  type MyPartner,
  type PartnerHour,
} from '../lib/admin';
import { PartnerArticles } from './PartnerArticles';
import { PartnerContributions } from './PartnerContributions';
import scannerScreen from '../assets/partners/scanner-screen.png';

export function PartnerSelf() {
  const { t, locale, setLocale, localePath } = useI18n();
  const { accessToken, user, signOut } = useAuth();
  const [partner, setPartner] = useState<MyPartner | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [signoutOpen, setSignoutOpen] = useState(false);
  const [signoutBusy, setSignoutBusy] = useState(false);

  // Admin impersonation: `/partner?as=<partnerId>` lets an admin act
  // against any partner's record. The backend ignores `as` for plain
  // partner roles, but we gate the wire too — sending it from a partner
  // session would be confusing in logs even if it does nothing.
  const asPartnerId = (() => {
    if (user?.role !== 'admin') return undefined;
    const qs = new URLSearchParams(location.search);
    const raw = qs.get('as');
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();

  async function load() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      const p = await getMyPartner(accessToken, asPartnerId);
      setPartner(p);
    } catch (e: any) {
      if (e?.code === 'not_partner' || e?.code === 'no_partner_link') {
        setPartner(null);
      } else {
        setErr(e?.message || `${e}`);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [accessToken, asPartnerId]);

  return (
    <RequirePartner>
      <div class="partner-page">
        <div class="partner-shell">
          <header class="partner-header">
            <a class="partner-back" href={localePath()}>{t('partner.back')}</a>
            <div class="partner-header-actions">
              <a class="partner-link" href={localePath('/play')}>{t('partner.to_game')}</a>
              <div class="partner-lang" role="group" aria-label="Language">
                {LOCALES.map((l: Locale) => (
                  <button
                    key={l}
                    type="button"
                    class={l === locale ? 'active' : ''}
                    aria-pressed={l === locale}
                    onClick={() => setLocale(l)}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                class="partner-link partner-signout"
                onClick={() => setSignoutOpen(true)}
              >
                {t('partner.signout')}
              </button>
            </div>
          </header>

          <h1 class="partner-title">{t('partner.title')}</h1>

          {asPartnerId != null && (
            <div class="partner-admin-banner">
              <strong>{t('partner.admin_preview').replace('{id}', String(asPartnerId))}</strong>
              {' '}
              <a class="partner-link" href={localePath('/admin/partners')}>
                ← {t('partner.admin_preview_back')}
              </a>
            </div>
          )}

          {loading && <p class="partner-muted">{t('partner.loading')}</p>}
          {!loading && err && <p class="partner-error">{err}</p>}

          {!loading && !err && !partner && (
            <div class="partner-card">
              <h2 class="partner-card-title">{t('partner.not_linked_title')}</h2>
              <p class="partner-muted">{t('partner.not_linked_body')}</p>
            </div>
          )}

          {!loading && partner && accessToken && (
            <>
              <PartnerForm
                partner={partner}
                accessToken={accessToken}
                as={asPartnerId}
                onSaved={(p) => setPartner(p)}
              />
              <PartnerContributions
                accessToken={accessToken}
                as={asPartnerId}
                remainingLitres={partner.remainingLitres ?? null}
              />
              <PartnerArticles accessToken={accessToken} as={asPartnerId} />
              <RedemptionFlow />
              <EdgeCases />
            </>
          )}
        </div>

        {signoutOpen && (
          <div
            class="partner-overlay"
            onClick={() => !signoutBusy && setSignoutOpen(false)}
          >
            <div
              class="partner-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="partner-signout-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="partner-signout-title" class="partner-card-title">
                {t('partner.signout_confirm_title')}
              </h2>
              <p class="partner-muted">{t('partner.signout_confirm_body')}</p>
              <div class="partner-modal-actions">
                <button
                  type="button"
                  class="partner-link"
                  disabled={signoutBusy}
                  onClick={() => setSignoutOpen(false)}
                >
                  {t('partner.signout_confirm_cancel')}
                </button>
                <button
                  type="button"
                  class="partner-primary"
                  disabled={signoutBusy}
                  onClick={async () => {
                    setSignoutBusy(true);
                    try {
                      await signOut();
                    } finally {
                      setSignoutBusy(false);
                      setSignoutOpen(false);
                    }
                  }}
                >
                  {t('partner.signout_confirm_yes')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequirePartner>
  );
}

function PartnerForm({
  partner,
  accessToken,
  as,
  onSaved,
}: {
  partner: MyPartner;
  accessToken: string;
  as?: number;
  onSaved: (p: MyPartner) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(partner.name);
  const [url, setUrl] = useState(partner.url ?? '');
  const [logoPath, setLogoPath] = useState(partner.logoPath ?? '');
  const [whiteBg, setWhiteBg] = useState(partner.whiteBg ?? false);
  const [address, setAddress] = useState(partner.address ?? '');
  const [lat, setLat] = useState(partner.lat?.toString() ?? '');
  const [lng, setLng] = useState(partner.lng?.toString() ?? '');
  const [redemptionCode, setRedemptionCode] = useState(partner.redemptionCode ?? '');
  const [servesThird, setServesThird] = useState(partner.servesThird);
  const [servesHalf, setServesHalf] = useState(partner.servesHalf);
  const [servesPint, setServesPint] = useState(partner.servesPint);
  const [hours, setHours] = useState<PartnerHour[]>(partner.hours ?? []);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  async function onLogoFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const res = await uploadFile(accessToken, file, 'partners');
      setLogoPath(res.url);
    } catch (e: any) {
      setErr(e?.message || t('partner.venue.upload_failed'));
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  // Toggles autosave: drink-size flips and redemption-code/venue edits all
  // share this submitter. The toggle handlers call it eagerly so the change
  // sticks even if the user navigates away before pressing Save. We pass
  // remainingLitres through unchanged — the partner UI doesn't expose it,
  // but the backend treats `undefined` as "clear", which would nuke the
  // live stock on every save. Echoing the current value preserves it.
  async function persist(next: {
    servesThird?: boolean;
    servesHalf?: boolean;
    servesPint?: boolean;
    whiteBg?: boolean;
  }) {
    setErr(null);
    try {
      const payload = {
        name: name.trim(),
        url: url.trim() || undefined,
        logoPath: logoPath.trim() || undefined,
        whiteBg: next.whiteBg ?? whiteBg,
        address: address.trim() || undefined,
        lat: lat.trim() === '' ? null : Number(lat),
        lng: lng.trim() === '' ? null : Number(lng),
        redemptionCode: redemptionCode.trim() || undefined,
        remainingLitres: partner.remainingLitres ?? null,
        servesThird: next.servesThird ?? servesThird,
        servesHalf: next.servesHalf ?? servesHalf,
        servesPint: next.servesPint ?? servesPint,
      };
      const updated = await updateMyPartner(accessToken, payload, as);
      onSaved(updated);
    } catch (e: any) {
      setErr(e?.message || t('partner.venue.save_failed'));
    }
  }

  function onToggle(which: 'third' | 'half' | 'pint', v: boolean) {
    if (which === 'third') {
      setServesThird(v);
      void persist({ servesThird: v });
    } else if (which === 'half') {
      setServesHalf(v);
      void persist({ servesHalf: v });
    } else {
      setServesPint(v);
      void persist({ servesPint: v });
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaved(false);
    setSaving(true);
    try {
      if (!name.trim()) throw new Error(t('partner.venue.name_required'));
      const latStr = lat.trim();
      const lngStr = lng.trim();
      if ((latStr === '') !== (lngStr === '')) {
        throw new Error(t('partner.venue.lat_lng_pair'));
      }
      const latNum = latStr === '' ? null : Number(latStr);
      const lngNum = lngStr === '' ? null : Number(lngStr);
      if (latNum !== null && (isNaN(latNum) || latNum < -90 || latNum > 90)) {
        throw new Error(t('partner.venue.lat_range'));
      }
      if (lngNum !== null && (isNaN(lngNum) || lngNum < -180 || lngNum > 180)) {
        throw new Error(t('partner.venue.lng_range'));
      }
      const code = redemptionCode.trim();
      if (code !== '' && !/^[A-Za-z0-9-]{3,12}$/.test(code)) {
        throw new Error(t('partner.redemption.format_error'));
      }
      const hv = validateHours(hours, t);
      if (!hv.ok) throw new Error(hv.error);
      const updated = await updateMyPartner(accessToken, {
        name: name.trim(),
        url: url.trim() || undefined,
        logoPath: logoPath.trim() || undefined,
        whiteBg,
        address: address.trim() || undefined,
        lat: latNum,
        lng: lngNum,
        redemptionCode: code || undefined,
        remainingLitres: partner.remainingLitres ?? null,
        servesThird,
        servesHalf,
        servesPint,
        hours: hv.hours,
      }, as);
      onSaved(updated);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.message || t('partner.venue.save_failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="partner-form" onSubmit={submit}>
      {/* ── Venue card ───────────────────────────────────────────────── */}
      <section class="partner-card">
        <h2 class="partner-card-title">{t('partner.venue.title')}</h2>
        <p class="partner-muted">{t('partner.venue.lede')}</p>

        {partner.infoLocked && (
          <div class="partner-locked-banner" role="status">
            <strong>{t('partner.venue.locked_banner_title')}</strong>
            <p>
              {t('partner.venue.locked_banner_body').split('{email}').map((part, i, arr) =>
                i < arr.length - 1
                  ? <>{part}<a href="mailto:info@insert-coin.io">info@insert-coin.io</a></>
                  : <>{part}</>
              )}
            </p>
          </div>
        )}

        <dl class="partner-meta">
          <div>
            <dt>{t('partner.venue.slug')}</dt>
            <dd class="partner-mono">{partner.slug}</dd>
          </div>
          {partner.canton && (
            <div>
              <dt>{t('partner.venue.canton')}</dt>
              <dd>{partner.canton}</dd>
            </div>
          )}
          <div>
            <dt>{t('partner.venue.status')}</dt>
            <dd>{partner.active ? t('partner.venue.status_active') : t('partner.venue.status_inactive')}</dd>
          </div>
        </dl>

        <label class="partner-field">
          <span>{t('partner.venue.name')} *{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
          <input
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required
            disabled={partner.infoLocked}
          />
        </label>

        <label class="partner-field">
          <span>{t('partner.venue.url')}{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
          <input
            type="url"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder={t('partner.venue.url_placeholder')}
            disabled={partner.infoLocked}
          />
        </label>

        <div class="partner-field">
          <span class="partner-field-label">{t('partner.venue.logo')}{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
          <div class="partner-logo-row">
            {logoPath && (
              <img
                src={logoPath}
                alt=""
                class={`partner-logo-preview ${whiteBg ? '' : 'is-on-dark'}`}
              />
            )}
            <div class="partner-logo-controls">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploading || partner.infoLocked}
                onChange={onLogoFile}
              />
              <input
                value={logoPath}
                onInput={(e) => setLogoPath((e.target as HTMLInputElement).value)}
                placeholder="https://…"
                class="partner-mono partner-input-mono"
                disabled={partner.infoLocked}
              />
              {uploading && <p class="partner-muted partner-uploading">{t('partner.venue.uploading')}</p>}
              <ToggleRow
                label={t('partner.venue.white_bg')}
                value={whiteBg}
                disabled={partner.infoLocked}
                onChange={(v) => {
                  setWhiteBg(v);
                  void persist({ whiteBg: v });
                }}
              />
              <p class="partner-muted partner-uploading">{t('partner.venue.white_bg_help')}</p>
            </div>
          </div>
        </div>

        <label class="partner-field">
          <span>{t('partner.venue.address')}{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
          <input
            value={address}
            onInput={(e) => setAddress((e.target as HTMLInputElement).value)}
            placeholder={t('partner.venue.address_placeholder')}
            disabled={partner.infoLocked}
          />
        </label>

        <div class="partner-row-2">
          <label class="partner-field">
            <span>{t('partner.venue.lat')}{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
            <input
              value={lat}
              inputMode="decimal"
              onInput={(e) => setLat((e.target as HTMLInputElement).value)}
              placeholder="46.519962"
              disabled={partner.infoLocked}
            />
          </label>
          <label class="partner-field">
            <span>{t('partner.venue.lng')}{partner.infoLocked && <em class="partner-locked-hint"> · {t('partner.venue.locked_hint')}</em>}</span>
            <input
              value={lng}
              inputMode="decimal"
              onInput={(e) => setLng((e.target as HTMLInputElement).value)}
              placeholder="6.633597"
              disabled={partner.infoLocked}
            />
          </label>
        </div>

        {err && <p class="partner-error">{err}</p>}
        {saved && <p class="partner-saved">{t('partner.venue.saved')}</p>}

        {!partner.infoLocked && (
          <div class="partner-actions">
            <button type="submit" class="partner-primary" disabled={saving}>
              {saving ? t('partner.venue.saving') : t('partner.venue.save')}
            </button>
          </div>
        )}
      </section>

      {/* ── Drink sizes card ─────────────────────────────────────────── */}
      <section class="partner-card">
        <h2 class="partner-card-title">{t('partner.sizes.title')}</h2>
        <p class="partner-muted">{t('partner.sizes.lede')}</p>
        <div class="partner-toggles">
          <ToggleRow label={t('partner.sizes.third')} value={servesThird} onChange={(v) => onToggle('third', v)} />
          <ToggleRow label={t('partner.sizes.half')}  value={servesHalf}  onChange={(v) => onToggle('half',  v)} />
          <ToggleRow label={t('partner.sizes.pint')}  value={servesPint}  onChange={(v) => onToggle('pint',  v)} />
        </div>
      </section>

      {/* ── Opening hours card ────────────────────────────────────────
          Hours are operational (terrace open in summer, etc.), so they
          stay editable even when the venue's identity fields are
          info_locked. The backend mirrors this. */}
      <section class="partner-card">
        <h2 class="partner-card-title">{t('partner.hours.title')}</h2>
        <p class="partner-muted">{t('partner.hours.lede')}</p>
        <PartnerHoursEditor hours={hours} onChange={setHours} />
        <div class="partner-actions">
          <button type="submit" class="partner-primary" disabled={saving}>
            {saving ? t('partner.venue.saving') : t('partner.venue.save')}
          </button>
        </div>
      </section>

      {/* ── Redemption code card ─────────────────────────────────────── */}
      <section class="partner-card">
        <h2 class="partner-card-title">{t('partner.redemption.title')}</h2>
        <p class="partner-muted">{t('partner.redemption.lede')}</p>
        <label class="partner-field">
          <span>{t('partner.redemption.label')}</span>
          <input
            value={redemptionCode}
            onInput={(e) => setRedemptionCode((e.target as HTMLInputElement).value)}
            placeholder={t('partner.redemption.placeholder')}
            autocomplete="off"
            spellcheck={false}
            class="partner-input-mono"
          />
        </label>
        <div class="partner-actions">
          <button type="submit" class="partner-primary" disabled={saving}>
            {saving ? t('partner.venue.saving') : t('partner.venue.save')}
          </button>
        </div>
      </section>
    </form>
  );
}

function RedemptionFlow() {
  const { t } = useI18n();
  const steps = [1, 2, 3, 4] as const;
  return (
    <section class="partner-card partner-flow">
      <h2 class="partner-card-title">{t('partner.flow.title')}</h2>
      <p class="partner-muted">{t('partner.flow.lede')}</p>
      <div class="partner-flow-grid">
        <ol class="partner-flow-steps">
          {steps.map((n) => (
            <li key={n} class="partner-flow-step">
              <span class="partner-flow-role">{t(`partner.flow.step${n}_role`)}</span>
              <span class="partner-flow-num" aria-hidden="true">{n}</span>
              <div class="partner-flow-text">
                <h3>{t(`partner.flow.step${n}_title`)}</h3>
                <p>{t(`partner.flow.step${n}_body`)}</p>
              </div>
            </li>
          ))}
        </ol>
        <div class="partner-flow-phone" aria-hidden="true">
          <img src={scannerScreen} alt={t('partner.flow.phone_alt')} />
        </div>
      </div>
    </section>
  );
}

function EdgeCases() {
  const { t } = useI18n();
  return (
    <section class="partner-card partner-edge">
      <h2 class="partner-card-title">{t('partner.edge.title')}</h2>
      <p class="partner-muted">{t('partner.edge.lede')}</p>
      <div class="partner-edge-grid">
        <div class="partner-edge-case">
          <span class="partner-edge-icon" aria-hidden="true">
            {/* QR with a slash through it */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3M20 14v7M14 20h3" />
              <line x1="3" y1="21" x2="21" y2="3" stroke-width="2" />
            </svg>
          </span>
          <div>
            <h3>{t('partner.edge.qr_title')}</h3>
            <p>{t('partner.edge.qr_body')}</p>
          </div>
        </div>
        <div class="partner-edge-case is-blocked">
          <span class="partner-edge-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M5.5 5.5l13 13" />
            </svg>
          </span>
          <div>
            <h3>{t('partner.edge.redeemed_title')}</h3>
            <p>{t('partner.edge.redeemed_body')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ToggleRow({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label class={`partner-toggle ${value ? 'on' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <span class="partner-toggle-label">{label}</span>
      <span class="partner-toggle-switch" aria-hidden="true">
        <span class="partner-toggle-thumb" />
      </span>
      <input
        type="checkbox"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
        class="partner-toggle-input"
      />
    </label>
  );
}
